import { createSelector } from "@reduxjs/toolkit";

import * as CLOUD_API from "@skydio/api_util/src/backends/cloud_api";
import data_level_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/data_level_pb";

import { getRouteOrgUuid } from "state/router";

import type { AppState } from "state/store";

const { orgPermissionEnum } = CLOUD_API.modules.organizations.utils;

export const SKYDIO_ORG_UUID = "627dec4f-3c91-4a82-88c2-81345e8dcd35";

export const getOrganization = (state: AppState) => state.currentUser.organization;
export const getCurrentUser = (state: AppState) => state.currentUser.user;
const getRequestedOrgUuid = (state: AppState) => state.currentUser.requestedOrgUuid;
export const getOrgUuid = createSelector(
  getRequestedOrgUuid,
  getRouteOrgUuid,
  (orgUuid, routeOrgUuid) => orgUuid || routeOrgUuid || ""
);

export const getCurrentUserEmail = createSelector(getCurrentUser, user => user?.email ?? "");
export const getCurrentUserId = createSelector(getCurrentUser, user => user?.uuid ?? "");

// don't use this, don't export it. frontend group checks make permissions management unnecessarily
// difficult.
const getCurrentUserGroups = createSelector(
  getCurrentUser,
  user => user?.groupsList.concat(user?.organizationGroupsList).map(group => group.name) ?? []
);

export const getCurrentUserResourceGroupLinks = createSelector(
  getCurrentUser,
  user => user?.resourceGroupLinksList ?? []
);

export const getUserOrgUuid = createSelector(getCurrentUser, user => user?.organizationId ?? "");
export const getUserOrgName = createSelector(getCurrentUser, user => user?.organizationName ?? "");

const getOrgUsers = createSelector(getOrganization, org => org?.usersList ?? []);
export const getOrgUserEmails = createSelector(getOrgUsers, users =>
  users.map(({ email }) => email)
);

export const getCurrentUserPermission = createSelector(
  getCurrentUser,
  user => user?.organizationPermission
);

export const isUserInSkydioOrg = createSelector(
  [getUserOrgUuid],
  userOrgUuid => userOrgUuid === SKYDIO_ORG_UUID
);

export const canUserAuditOtherOrgs = isUserInSkydioOrg;

export const isUserAtLeastModerator = createSelector(
  getCurrentUserPermission,
  canUserAuditOtherOrgs,
  getCurrentUserResourceGroupLinks,
  (perm, canAudit, resourceGroupLinks) => {
    if (canAudit) {
      return canAudit;
    } else if (resourceGroupLinks.length > 0) {
      // Only resource group permissions allowed are MODERATOR and above
      return true;
    } else {
      return perm ? perm >= orgPermissionEnum.MODERATOR : false;
    }
  }
);

// Returns true if a user has read access to all Resource Groups of an org,
// either as a Skydio admin, or as someone granted Moderator / Cloud User
// access to the Entire Organization
export const isUserAtLeastEntireOrgModerator = createSelector(
  getCurrentUserPermission,
  canUserAuditOtherOrgs,
  getCurrentUserResourceGroupLinks,
  (perm, canAudit, resourceGroupLinks) => {
    if (canAudit) {
      return true;
    } else if (
      resourceGroupLinks.find(link => {
        return (
          link.isOrganizationDefault && link.resourceGroupPermission >= orgPermissionEnum.MODERATOR
        );
      })
    ) {
      return true;
    } else {
      return perm ? perm >= orgPermissionEnum.MODERATOR : false;
    }
  }
);

// Is this user a Remote Pilot of the current org
export const isUserOrgRemotePilot = createSelector(
  getCurrentUserPermission,
  getOrganization,
  getUserOrgUuid,
  (perm, org, user_org_uuid) =>
    perm === orgPermissionEnum.REMOTE_PILOT && org?.uuid === user_org_uuid
);

// Is this user an Admin of the current org
export const isUserOrgAdmin = createSelector(
  getCurrentUserPermission,
  getOrganization,
  getUserOrgUuid,
  (perm, org, user_org_uuid) =>
    (perm === orgPermissionEnum.ADMIN || perm === orgPermissionEnum.SKYDIO_SUPERUSER) &&
    org?.uuid === user_org_uuid
);

// Is this user an Admin of the current org, OR a skydio admin that can audit other orgs
export const isUserAdmin = createSelector(
  isUserOrgAdmin,
  canUserAuditOtherOrgs,
  (userIsOrgAdmin, canAuditAdmin) => (canAuditAdmin ? canAuditAdmin : userIsOrgAdmin)
);

export const isUserSkydioSuperuser = createSelector(
  getCurrentUserPermission,
  getOrganization,
  getUserOrgUuid,
  (perm, org, user_org_uuid) =>
    perm === orgPermissionEnum.SKYDIO_SUPERUSER && org?.uuid === user_org_uuid
);

export const isUserSkydioReadonly = createSelector(
  getCurrentUserPermission,
  getOrganization,
  getUserOrgUuid,
  (perm, org, user_org_uuid) =>
    perm === orgPermissionEnum.SKYDIO_READONLY && org?.uuid === user_org_uuid
);

export const isUserSkydioInOrg = createSelector(
  isUserSkydioSuperuser,
  isUserSkydioReadonly,
  (superuser, readonly) => superuser || readonly
);

// Do not proliferate this pattern; frontend group checks make permissions management unnecessarily
// difficult. FIXME: provide permission validation tooling with which to eliminate this check.
export const canUserSetProtectedStatus = createSelector(
  getCurrentUserGroups,
  getCurrentUserEmail,
  (groupsList, email) => {
    return groupsList.includes("product_ops_manager") && email.endsWith("@skydio.com");
  }
);

export const getOrgName = createSelector(
  getOrganization,
  getUserOrgName,
  (organization, userOrgName) => {
    return organization ? organization.name : userOrgName;
  }
);

// User is currently viewing an internal org
export const isUserInInternalOrg = createSelector(getOrganization, organization => {
  return organization?.dataLevel === data_level_pb.DataLevel.LevelEnum.INTERNAL;
});

export const getAvailableSwitches = createSelector(getOrganization, org => org?.switchesList ?? []);

// This is a best-guess check for whether this user _may_ be able to be a pilot.
// It doesn't perform any resource-group specific checks, so it may return true even if the user
// cannot actually be a pilot for the current vehicle.
export const hasPilotPermission = createSelector(
  getCurrentUserPermission,
  getCurrentUserResourceGroupLinks,
  (currentUserPermission, resourceGroupLinks) =>
    // There shouldn't be any users who have user orgPermissionEnum.REMOTE_PILOT; it's not a supported
    // permission level. But there are, so we'll let it go here.
    currentUserPermission === orgPermissionEnum.REMOTE_PILOT ||
    currentUserPermission === orgPermissionEnum.ADMIN ||
    currentUserPermission === orgPermissionEnum.SKYDIO_SUPERUSER ||
    resourceGroupLinks.some(link => link.resourceGroupPermission === orgPermissionEnum.REMOTE_PILOT)
);
